/**
 * Safe storage utility to handle environments where localStorage/sessionStorage 
 * might be unavailable or restricted (e.g., Incognito mode, iframes).
 * Provides an in-memory fallback to prevent errors.
 */

import logger from './logger';

// In-memory storage fallback
class MemoryStorage {
  constructor() {
    this.store = new Map();
  }

  getItem(key) {
    return this.store.get(key) || null;
  }

  setItem(key, value) {
    this.store.set(key, String(value));
  }

  removeItem(key) {
    this.store.delete(key);
  }

  clear() {
    this.store.clear();
  }
}

const isStorageAvailable = (type) => {
  try {
    // Check if window is defined (SSR safety)
    if (typeof window === 'undefined') return false;
    
    const storage = window[type];
    if (!storage) return false;
    
    const x = '__storage_test__';
    storage.setItem(x, x);
    storage.removeItem(x);
    return true;
  } catch (e) {
    return false;
  }
};

// Initialize fallbacks
const localMemory = new MemoryStorage();
const sessionMemory = new MemoryStorage();

const canUseLocalStorage = isStorageAvailable('localStorage');
const canUseSessionStorage = isStorageAvailable('sessionStorage');

export const safeLocalStorage = {
  getItem: (key) => {
    if (!canUseLocalStorage) return localMemory.getItem(key);
    try {
      return localStorage.getItem(key);
    } catch (e) {
      logger.warn('Error accessing localStorage:', e);
      return localMemory.getItem(key);
    }
  },
  setItem: (key, value) => {
    if (!canUseLocalStorage) {
      localMemory.setItem(key, value);
      return;
    }
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      logger.warn('Error setting localStorage:', e);
      localMemory.setItem(key, value);
    }
  },
  removeItem: (key) => {
    if (!canUseLocalStorage) {
      localMemory.removeItem(key);
      return;
    }
    try {
      localStorage.removeItem(key);
    } catch (e) {
      logger.warn('Error removing from localStorage:', e);
      localMemory.removeItem(key);
    }
  },
  clear: () => {
    if (!canUseLocalStorage) {
      localMemory.clear();
      return;
    }
    try {
      localStorage.clear();
    } catch (e) {
      logger.warn('Error clearing localStorage:', e);
      localMemory.clear();
    }
  }
};

export const safeSessionStorage = {
  getItem: (key) => {
    if (!canUseSessionStorage) return sessionMemory.getItem(key);
    try {
      return sessionStorage.getItem(key);
    } catch (e) {
      logger.warn('Error accessing sessionStorage:', e);
      return sessionMemory.getItem(key);
    }
  },
  setItem: (key, value) => {
    if (!canUseSessionStorage) {
      sessionMemory.setItem(key, value);
      return;
    }
    try {
      sessionStorage.setItem(key, value);
    } catch (e) {
      logger.warn('Error setting sessionStorage:', e);
      sessionMemory.setItem(key, value);
    }
  },
  removeItem: (key) => {
    if (!canUseSessionStorage) {
      sessionMemory.removeItem(key);
      return;
    }
    try {
      sessionStorage.removeItem(key);
    } catch (e) {
      logger.warn('Error removing from sessionStorage:', e);
      sessionMemory.removeItem(key);
    }
  },
  clear: () => {
    if (!canUseSessionStorage) {
      sessionMemory.clear();
      return;
    }
    try {
      sessionStorage.clear();
    } catch (e) {
      logger.warn('Error clearing sessionStorage:', e);
      sessionMemory.clear();
    }
  }
};
